import { computed, Injectable, signal } from '@angular/core';
import { MerchantEnum } from '../../features/redeem/redeem';

const STORAGE_KEY = 'current_merchant';

export const DEFAULT_BANNER = '/banner.jpg';

/** Maps a MerchantEnum key (e.g. 'JUSTJUICE') to its banner image path. */
const MERCHANT_BANNERS: Record<keyof typeof MerchantEnum, string> = {
  JUSTJUICE: '/banners/justjuice.jpg',
  JUSTFRUIT: '/banners/justfruit.jpg',
  ITIMLAMOON: '/banners/itimlamoon.jpg',
  MONSOONTEA: '/banners/monsoontea.jpg',
  THEPORTTHAI: '/banners/theportthai.jpg',
  LAFIORE: '/banners/lafiore.jpg',
  LAFIORECOFFEE: '/banners/lafiorecoffee.jpg',
};

function isMerchantKey(key: string): key is keyof typeof MerchantEnum {
  return key in MERCHANT_BANNERS;
}

@Injectable({ providedIn: 'root' })
export class MerchantContext {
  private readonly merchantKeySignal = signal<keyof typeof MerchantEnum | null>(
    this.readFromStorage(),
  );

  /** The active merchant's route-param key (e.g. 'JUSTJUICE'), or null if none is set. */
  readonly merchantKey = this.merchantKeySignal.asReadonly();

  readonly bannerUrl = computed(() => {
    const key = this.merchantKeySignal();
    return key ? MERCHANT_BANNERS[key] : DEFAULT_BANNER;
  });

  /** Sets the active merchant from a route param value; falls back to the default banner if unknown. */
  setMerchant(key: string | null | undefined): void {
    const normalized = key && isMerchantKey(key) ? key : null;
    if (normalized === this.merchantKeySignal()) return;

    this.merchantKeySignal.set(normalized);
    this.writeToStorage(normalized);
  }

  private readFromStorage(): keyof typeof MerchantEnum | null {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored && isMerchantKey(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  private writeToStorage(key: keyof typeof MerchantEnum | null): void {
    try {
      if (key) {
        sessionStorage.setItem(STORAGE_KEY, key);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // sessionStorage may be unavailable (e.g. private browsing); ignore.
    }
  }
}
